import React from 'react';
import { Button } from '@/components/shared';

export type UserPath = 'senior' | 'volunteer' | 'professional' | null;

interface PathSelectorProps {
    selectedPath: UserPath;
    onPathSelect: (path: UserPath) => void;
    className?: string;
}

export const PathSelector: React.FC<PathSelectorProps> = ({
    selectedPath,
    onPathSelect,
    className = ''
}) => {
    const paths = [
        {
            id: 'senior' as const,
            title: 'I need help with daily tasks',
            subtitle: 'Learn how BiBi can support your independence',
            description: 'Transportation, errands, home tasks, and companionship',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
            ),
            color: 'from-blue-500 to-blue-600',
            hoverColor: 'hover:from-blue-600 hover:to-blue-700'
        },
        {
            id: 'volunteer' as const,
            title: 'I want to help my community',
            subtitle: 'Discover how to make a meaningful difference',
            description: 'Join our network of caring volunteers',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            ),
            color: 'from-green-500 to-green-600',
            hoverColor: 'hover:from-green-600 hover:to-green-700'
        },
        {
            id: 'professional' as const,
            title: 'I offer skilled help',
            subtitle: 'Learn about partnership opportunities',
            description: 'Use your professional skills to support seniors in your community. A great fit for handymen, gig workers, and local businesses.',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                </svg>
            ),
            color: 'from-purple-500 to-purple-600',
            hoverColor: 'hover:from-purple-600 hover:to-purple-700'
        }
    ];

    return (
        <section className={`py-16 bg-white ${className}`} aria-labelledby="path-selector-heading">
            <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                    <h2
                        id="path-selector-heading"
                        className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
                    >
                        Select your Role to Learn More
                    </h2>
                </div>

                <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto md:items-stretch">
                    {paths.map((path) => (
                        <Button
                            key={path.id}
                            onClick={() => onPathSelect(path.id)}
                            variant="custom"
                            className={`
                                relative group bg-gradient-to-br ${path.color} ${path.hoverColor}
                                text-white rounded-xl p-8 transition-all duration-300 transform hover:scale-105
                                focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-opacity-50
                                h-full flex flex-col
                                ${selectedPath === path.id ? 'ring-4 ring-white ring-opacity-30 scale-105' : ''}
                            `}
                            aria-pressed={selectedPath === path.id}
                        >
                            {/* Icon */}
                            <div className="flex justify-center mb-4">
                                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                                    {path.icon}
                                </div>
                            </div>

                            {/* Content */}
                            <div className="text-center flex-grow flex flex-col">
                                <h3 className="text-xl font-bold mb-2">{path.title}</h3>
                                <p className="text-white/90 text-sm font-medium mb-3">{path.subtitle}</p>
                                <p className="text-white/80 text-sm leading-relaxed flex-grow min-h-[3rem] flex items-center justify-center">{path.description}</p>
                            </div>

                            {/* Learn More Indicator */}
                            <div className="mt-6 flex items-center justify-center text-white/80">
                                <span className="text-sm">Click to learn more</span>
                                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                </svg>
                            </div>

                            {/* Selection Indicator */}
                            {selectedPath === path.id && (
                                <div className="absolute top-4 right-4">
                                    <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                </div>
                            )}
                        </Button>
                    ))}
                </div>

                {/* Change Path Button */}
                {selectedPath && (
                    <div className="text-center mt-8">
                        <Button
                            onClick={() => onPathSelect(null)}
                            variant="ghost"
                            size="sm"
                            className="text-bibi-blue hover:text-bibi-blue/80"
                        >
                            Choose a different role
                        </Button>
                    </div>
                )}
            </div>
        </section>
    );
};
